import React, { memo, useState } from 'react';
import FilterMenuSmartView from '../../FilterMenu/FilterMenuSmartView';
import useFilterMenu from '../../FilterMenu/shared/hooks/useFilterMenu';
import type { BrandFilterViewModel } from 'autogen/swagger/all';
import BooleanFilterOption from '../BooleanFilter/BooleanFilterOption';
import OptionsFilteredBySearchQuery from '../../Search/OptionsFilteredBySearchQuery';
import AlphabeticalHeader from '../../AlphabeticalHeader';
import { Typography } from 'components/generic/typography';
import styled, { css } from 'styled-components';
import { ColorNeutralGrey2, Spacing16, Spacing24, Spacing8 } from 'autogen/design-tokens/tokens';
import { useSearchQuery } from '../../Search/shared/hooks/useFilterOptionsBySearchQuery';
import { InlineButtonWithIcon } from 'components/generic/InlineButton';
import { useTranslation } from 'react-i18next';
import { SeeAll, SeeLess } from 'autogen/translation-keys/trans-website-shared';
import { MyBrandsFilterHeadline } from 'autogen/translation-keys/trans-website-filter';
import Icon from 'components/Icon';

const MyBrandsContainer = styled.div`
    display: grid;
    margin-bottom: ${Spacing24};    
`;

const StyledTitle = styled(Typography.Title)`
    padding: 0 ${Spacing16} ${Spacing8};
`;

const ButtonWrapper = styled.div`
    justify-self: end;
    padding: ${Spacing8} ${Spacing16};
`;

const MyBrandsOption = styled.div<{ $showAllMyBrands: boolean }>`
    &:nth-child(n+5) {
        display: none;
    }

    ${({ $showAllMyBrands }) =>
        $showAllMyBrands &&
        css`
             &:nth-child(n+5) {
                display: block;
            }
        `}
`;

const Divider = styled.div`
    background: ${ColorNeutralGrey2};
    height: 1px;
    width: 100%;
    margin-top: ${Spacing24};
`;

const BrandFilterMenu = ({
    model: {
        displayName,
        brands,
        brandClubs,
        queryStringName,
        showSearch,
        attribute: { id }
    }
}: {
    model: BrandFilterViewModel;
}) => {
    const filterMenuId = id.toString();
    const { closeFilterMenu } = useFilterMenu(filterMenuId);
    const [searchQuery] = useSearchQuery();
    const [showAllMyBrands, setShowAllMyBrands] = useState(false);
    const shouldDisplayAlphabeticalHeaders = brands.options.length >= 50;
    const shouldDisplayShowAllMyBrandsButton = brandClubs.options.length > 3;
    const { t } = useTranslation();

    const MyBrandsSection = (
        <MyBrandsContainer>
            <StyledTitle size="S" forwardedAs="h2">
                {t(MyBrandsFilterHeadline)}
            </StyledTitle>
            {brandClubs.options.map((option, i) => (
                <MyBrandsOption $showAllMyBrands={showAllMyBrands} key={i}>
                    <BooleanFilterOption
                        model={option}
                        queryStringName={queryStringName}
                        menuDisplayName={displayName}
                    />
                </MyBrandsOption>
            ))}
            {shouldDisplayShowAllMyBrandsButton && (
                <ButtonWrapper>
                    <InlineButtonWithIcon
                        fontWeight="semibold"
                        icon={
                            showAllMyBrands ? (
                                <Icon type="ChevronUp" size={16} />
                            ) : (
                                <Icon type="ChevronDown" size={16} />
                            )
                        }
                        iconProperties={{ position: 'right' }}
                        onClick={() => setShowAllMyBrands((prevState) => !prevState)}
                    >
                        {t(showAllMyBrands ? SeeLess : SeeAll)}
                    </InlineButtonWithIcon>
                </ButtonWrapper>
            )}
            {!shouldDisplayAlphabeticalHeaders && <Divider />}
        </MyBrandsContainer>
    );

    if (showSearch) {
        return (
            <FilterMenuSmartView
                withSearch
                name={filterMenuId}
                headline={displayName}
                backButtonCallback={closeFilterMenu}
            >
                <OptionsFilteredBySearchQuery options={brands.options}>
                    {(optionsFilteredBySearch) => (
                        <>
                            {brandClubs.options.length > 0 && !searchQuery && MyBrandsSection}
                            {optionsFilteredBySearch.map((option, i) => (
                                <React.Fragment key={i}>
                                    {shouldDisplayAlphabeticalHeaders && <AlphabeticalHeader option={option} />}
                                    <BooleanFilterOption
                                        model={option}
                                        queryStringName={queryStringName}
                                        menuDisplayName={displayName}
                                    />
                                </React.Fragment>
                            ))}
                        </>
                    )}
                </OptionsFilteredBySearchQuery>
            </FilterMenuSmartView>
        );
    }

    return (
        <FilterMenuSmartView name={filterMenuId} headline={displayName} backButtonCallback={closeFilterMenu}>
            {brandClubs.options.length > 0 && MyBrandsSection}
            {brands.options.map((option, i) => (
                <BooleanFilterOption
                    key={i}
                    model={option}
                    queryStringName={queryStringName}
                    menuDisplayName={displayName}
                />
            ))}
        </FilterMenuSmartView>
    );
};

export default memo(BrandFilterMenu);
